import { useRef, useEffect, type ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreVertical,
  Eye,
  UserPlus,
  FileText,
  Key,
  Loader2,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  deriveFlag,
  getDaysInMilestone,
  getDaysSinceLastActivity,
  getDaysStale
} from '../../activeLoanModel';
import type { ProspectLoan, ProspectSortConfig } from '../../types';
import type { LoanListStep } from '../loan-list-layout';
import { InitialsAvatar } from '../primitives/InitialsAvatar';
import { ChannelTag } from '../primitives/ChannelTag';
import { MilestoneTrack } from '../primitives/MilestoneTrack';
import { LockStatusPill } from '../primitives/LockStatusPill';
import { RiskFlag } from '../primitives/RiskFlag';
import { ActivityChip } from '../primitives/ActivityChip';
import { ConversationLogHoverCard } from '../primitives/ConversationLogHoverCard';
import { LoanNumberCell } from '../primitives/LoanNumberCell';
import { LoanListCard } from '../primitives/LoanListCard';
import { FLAG_STRIPE_CLASS, WRAPPING_PILL_CLASS } from '../primitives/loanRowClasses';

interface TableDirectionProps {
  prospects: ProspectLoan[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  sortConfig: ProspectSortConfig;
  onSort: (field: string) => void;
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  onViewDetails: (prospect: ProspectLoan) => void;
  onViewDocuments: (prospect: ProspectLoan) => void;
  onViewInvestorLock?: (prospect: ProspectLoan) => void;
  onAssignLO?: (prospectId: string) => void;
  onLoadMore?: () => void;
  /**
   * How the list presents itself in the room it has: every column, two-line
   * columns, or one card per loan. The list never scrolls sideways.
   */
  layout?: LoanListStep;
  /** Cards per row in the stacked layout. */
  cardColumns?: 1 | 2;
}

// Every sortable column. Loan Status is absent on purpose:
// search_encompass_loans has no cx_loan_status sort key and silently falls
// back to creation_date for unknown fields.
const SORT_FIELDS: ReadonlyArray<{ field: string; label: string }> = [
  { field: 'trans_details_loan_number', label: 'Loan #' },
  { field: 'borrower_last_name', label: 'Borrower' },
  { field: 'channel', label: 'Channel' },
  { field: 'milestone', label: 'Milestone' },
  { field: 'trans_details_loan_amt', label: 'Amount' },
  { field: 'property_address', label: 'Property' },
  { field: 'trans_details_loan_purpose', label: 'Purpose' },
  { field: 'loan_officer_last_name', label: 'Officer' },
  { field: 'rate_lock_status', label: 'Lock' },
  { field: 'flag_days', label: 'Flag' }
];

const HEADER_LABEL_CLASS = 'whitespace-nowrap text-[11px] font-medium uppercase tracking-wider text-muted-foreground';

export function TableDirection({
  prospects,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  totalCount = 0,
  sortConfig,
  onSort,
  selectedIds,
  onSelectIds,
  onViewDetails,
  onViewDocuments,
  onViewInvestorLock,
  onAssignLO,
  onLoadMore,
  layout = 'wide',
  cardColumns = 1
}: TableDirectionProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isStacked = layout === 'stacked';

  // Infinite scroll sentinel — same semantics as ProspectTable. The stacked
  // layout has no inner scroller, so it watches the viewport instead.
  useEffect(() => {
    if (!loadMoreRef.current || !onLoadMore || !hasMore || isLoadingMore) {
return;
}
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
onLoadMore();
}
      },
      { threshold: 0.1, root: isStacked ? null : parentRef.current }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isLoadingMore, isStacked]);

  const handleSelectAll = (checked: boolean) => {
    onSelectIds(checked ? prospects.map(p => p.id) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    onSelectIds(checked ? [...selectedIds, id] : selectedIds.filter(s => s !== id));
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortConfig.field !== field) {
return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />;
}
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0" /> : <ArrowDown className="h-3 w-3 flex-shrink-0" />;
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={cn('flex items-center gap-1 text-left hover:text-foreground transition-colors', HEADER_LABEL_CLASS)}
    >
      {children}
      <SortIcon field={field} />
    </button>
  );

  const renderActions = (prospect: ProspectLoan, alwaysVisible: boolean): ReactNode => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-7 w-7 p-0',
            // Hover-reveal only where a pointer is a safe assumption; the
            // narrower layouts are also what touch screens get.
            !alwaysVisible && 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity'
          )}
        >
          <span className="sr-only">Open actions</span>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onViewDetails(prospect)}>
          <Eye className="mr-2 h-4 w-4" /> View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onViewDocuments(prospect)}>
          <FileText className="mr-2 h-4 w-4" /> Documents
        </DropdownMenuItem>
        {onViewInvestorLock && prospect.rate_lock_investor_commitment && (
          <DropdownMenuItem onClick={() => onViewInvestorLock(prospect)}>
            <Key className="mr-2 h-4 w-4" /> View Investor Lock
          </DropdownMenuItem>
        )}
        {onAssignLO && !prospect.loan_officer_id && (
          <DropdownMenuItem onClick={() => onAssignLO(prospect.id)}>
            <UserPlus className="mr-2 h-4 w-4" /> Assign Loan Officer
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderRow = (prospect: ProspectLoan) => {
    const isSelected = selectedIds.includes(prospect.id);
    const isDeleted = prospect.deleted_from_encompass === true;
    const flag = deriveFlag(prospect);
    const daysInStage = getDaysInMilestone(prospect);
    const daysStale = getDaysStale(prospect);
    const daysSinceActivity = getDaysSinceLastActivity(prospect);
    const hasConversationLogs = (prospect.conversation_logs?.length ?? 0) > 0;
    const borrower = `${prospect.borrower_first_name ?? ''} ${prospect.borrower_last_name ?? ''}`.trim();
    const officerName = prospect.loan_officer_first_name && prospect.loan_officer_last_name
      ? `${prospect.loan_officer_first_name} ${prospect.loan_officer_last_name}`
      : null;
    // Synced verbatim from Encompass custom field CX.LOAN.STATUS by
    // encompass-loan-sync / encompass-webhook; not derived from milestone.
    const loanStatus = prospect.cx_loan_status?.trim() || null;
    const cityState = [prospect.subject_property_city, prospect.subject_property_state].filter(Boolean).join(', ');
    const isCompact = layout === 'compact';

    const borrowerControl = (
      <button
        type="button"
        onClick={event => {
          event.stopPropagation();
          onViewDetails(prospect);
        }}
        aria-label={borrower ? undefined : `Open loan ${prospect.trans_details_loan_number ?? ''}`.trim()}
        title={borrower || undefined}
        className={cn(
          'min-w-0 max-w-full rounded-sm text-left font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isCompact ? 'block break-words leading-snug' : 'truncate'
        )}
      >
        {borrower || '—'}
      </button>
    );

    const rowClassName = cn(
      'cursor-pointer group',
      isSelected && 'bg-muted/50',
      isDeleted && 'bg-red-50 hover:bg-red-100',
      !isDeleted && flag && FLAG_STRIPE_CLASS[flag]
    );

    const selectCheckbox = (
      <Checkbox
        checked={isSelected}
        onCheckedChange={(checked) => handleSelectOne(prospect.id, checked as boolean)}
        aria-label={`Select ${prospect.trans_details_loan_number}`}
      />
    );

    const loanNumber = (
      <div className="flex flex-wrap items-center gap-1.5 min-w-0">
        {isDeleted && <Trash2 className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
        <LoanNumberCell
          loanNumber={prospect.trans_details_loan_number}
          className={cn(isDeleted && 'text-red-700')}
          wrap
        />
        {isDeleted && <Badge variant="destructive" className="text-[10px]">Deleted</Badge>}
      </div>
    );

    // Wrap rather than truncate so long statuses stay fully readable
    // instead of being clipped at the next column boundary.
    const loanStatusText = (
      <span
        className={cn(
          'block whitespace-normal break-words leading-snug text-sm',
          loanStatus ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {loanStatus ?? '—'}
      </span>
    );

    // Wrap rather than truncate so values like "NoCash-Out Refinance" stay readable.
    const purposeText = (
      <span className="block whitespace-normal break-words leading-snug text-sm text-muted-foreground">
        {prospect.trans_details_loan_purpose || '—'}
      </span>
    );

    const officer = officerName ? (
      // In the two-line layout the name drops below the avatar before it has to break mid-word.
      <div className={cn('flex items-center gap-1.5 min-w-0', isCompact && 'flex-wrap')}>
        <InitialsAvatar name={officerName} size={20} />
        <span className={cn('text-sm', isCompact ? 'min-w-0 break-words' : 'truncate')}>
          {prospect.loan_officer_first_name}
        </span>
      </div>
    ) : (
      <span className="text-sm italic text-muted-foreground">Unassigned</span>
    );

    const pillClassName = isCompact ? WRAPPING_PILL_CLASS : undefined;
    const flagNode = flag === 'stalled' ? (
      <ConversationLogHoverCard loan={prospect} variant="stalled" days={daysStale}>
        <RiskFlag flag="stalled" daysStale={daysStale} className={pillClassName} />
      </ConversationLogHoverCard>
    ) : hasConversationLogs ? (
      <ConversationLogHoverCard loan={prospect} variant="active" days={daysSinceActivity}>
        <ActivityChip days={daysSinceActivity} className={pillClassName} />
      </ConversationLogHoverCard>
    ) : null;

    if (isCompact) {
      return (
        <TableRow key={prospect.id} className={rowClassName} onClick={() => onViewDetails(prospect)}>
          <TableCell onClick={e => e.stopPropagation()}>{selectCheckbox}</TableCell>
          <TableCell className={cn(isDeleted && 'text-red-700')}>
            <div className="flex items-start gap-2 min-w-0">
              <InitialsAvatar name={borrower} size={24} />
              <div className="min-w-0">
                {borrowerControl}
                {loanNumber}
              </div>
            </div>
          </TableCell>
          <TableCell>
            <MilestoneTrack loan={prospect} daysInMilestone={daysInStage} />
            <div className="mt-1">{loanStatusText}</div>
          </TableCell>
          <TableCell>
            <div className="break-words font-mono text-sm text-foreground">
              {formatCurrency(prospect.trans_details_loan_amt)}
            </div>
            {purposeText}
          </TableCell>
          <TableCell>
            <div className="break-words text-sm leading-snug">{prospect.property_address || '—'}</div>
            {cityState && <div className="break-words text-[11px] text-muted-foreground">{cityState}</div>}
          </TableCell>
          <TableCell>
            {officer}
            <div className="mt-1">
              <ChannelTag channel={prospect.channel} className={WRAPPING_PILL_CLASS} />
            </div>
          </TableCell>
          <TableCell>
            <LockStatusPill loan={prospect} className={WRAPPING_PILL_CLASS} />
            {flagNode && <div className="mt-1">{flagNode}</div>}
          </TableCell>
          <TableCell className="pl-0" onClick={e => e.stopPropagation()}>
            {renderActions(prospect, true)}
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={prospect.id} className={rowClassName} onClick={() => onViewDetails(prospect)}>
        <TableCell onClick={e => e.stopPropagation()}>{selectCheckbox}</TableCell>
        <TableCell
          className={cn(isDeleted && 'text-red-700')}
          onClick={e => e.stopPropagation()}
        >
          {loanNumber}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2 min-w-0">
            <InitialsAvatar name={borrower} size={24} />
            {borrowerControl}
          </div>
        </TableCell>
        <TableCell>
          <ChannelTag channel={prospect.channel} />
        </TableCell>
        <TableCell>
          <MilestoneTrack loan={prospect} daysInMilestone={daysInStage} />
        </TableCell>
        <TableCell>{loanStatusText}</TableCell>
        <TableCell className="font-mono text-sm text-foreground">
          {formatCurrency(prospect.trans_details_loan_amt)}
        </TableCell>
        <TableCell>
          <div className="min-w-0">
            <div className="truncate text-sm" title={prospect.property_address}>{prospect.property_address || '—'}</div>
            {cityState && <div className="truncate text-[11px] text-muted-foreground">{cityState}</div>}
          </div>
        </TableCell>
        <TableCell>{purposeText}</TableCell>
        <TableCell>{officer}</TableCell>
        <TableCell>
          <LockStatusPill loan={prospect} />
        </TableCell>
        <TableCell>{flagNode}</TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          {renderActions(prospect, false)}
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading && prospects.length === 0) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-muted/60 animate-pulse rounded" />)}
      </div>
    );
  }

  if (prospects.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground text-lg">No loans match the current filters</p>
        <p className="text-muted-foreground text-sm mt-2">
          Try adjusting your filters or clearing the active segment.
        </p>
      </div>
    );
  }

  const allSelected = prospects.length > 0 && selectedIds.length === prospects.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < prospects.length;
  const selectAllState = someSelected ? 'indeterminate' : allSelected;
  const activeSort = SORT_FIELDS.find(sort => sort.field === sortConfig.field);
  const loadingClassName = isLoading && prospects.length > 0 && 'opacity-50 pointer-events-none transition-opacity';

  const loadMoreSentinel = hasMore && (
    <div ref={loadMoreRef} className="flex justify-center py-4">
      {isLoadingMore && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading more…</span>
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="border-t px-4 py-2 text-sm text-muted-foreground flex flex-wrap items-center justify-between gap-2">
      <span>Showing {prospects.length.toLocaleString()} of {totalCount.toLocaleString()} loans</span>
      {hasMore && !isLoadingMore && (
        <Button variant="ghost" size="sm" onClick={onLoadMore} className="text-primary">
          Load more
        </Button>
      )}
    </div>
  );

  if (isStacked) {
    return (
      <div className="border rounded-lg flex min-w-0 flex-col bg-background">
        {/* Cards have no column headers, so selection and sorting move here. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked={selectAllState} onCheckedChange={handleSelectAll} />
            Select all
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-auto min-h-9 max-w-full whitespace-normal text-left"
                // The direction is shown only as an arrow, so spell it out for
                // screen readers. The visible text stays the start of the name.
                aria-label={activeSort
                  ? `Sort: ${activeSort.label}, ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`
                  : undefined}
              >
                <ArrowUpDown className="mr-2 flex-shrink-0" />
                {activeSort ? `Sort: ${activeSort.label}` : 'Sort'}
                {activeSort && (
                  sortConfig.direction === 'asc'
                    ? <ArrowUp className="ml-2 flex-shrink-0" />
                    : <ArrowDown className="ml-2 flex-shrink-0" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuRadioGroup value={sortConfig.field}>
                {SORT_FIELDS.map(sort => (
                  // onSelect, not the group's onValueChange: choosing the
                  // active field again reverses its direction, the same as
                  // clicking a column header twice.
                  <DropdownMenuRadioItem key={sort.field} value={sort.field} onSelect={() => onSort(sort.field)}>
                    {sort.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div ref={parentRef} className={cn('p-2', loadingClassName)}>
          <ul
            role="list"
            aria-label="Loans"
            className={cn('grid gap-2', cardColumns === 2 ? 'grid-cols-2' : 'grid-cols-1')}
          >
            {prospects.map(prospect => (
              <li key={prospect.id} className="min-w-0">
                <LoanListCard
                  loan={prospect}
                  onOpen={onViewDetails}
                  selection={{
                    selected: selectedIds.includes(prospect.id),
                    onChange: checked => handleSelectOne(prospect.id, checked)
                  }}
                  actions={renderActions(prospect, true)}
                />
              </li>
            ))}
          </ul>
          {loadMoreSentinel}
        </div>

        {footer}
      </div>
    );
  }

  const isCompact = layout === 'compact';

  return (
    <div className="border rounded-lg flex min-w-0 flex-col bg-background">
      <div
        ref={parentRef}
        className="overflow-y-auto rounded-t-lg"
        style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
      >
        {/* One fixed-layout table: column widths are shares of the available
            width, so the header and rows stay aligned and always fit. The
            wrapper is not a scroller, which lets the header stick to the
            vertical scroller above. Keyed by layout so a new column set gets
            a new table: a fixed table reused across column sets can keep
            stale column widths. */}
        <Table key={layout} className="table-fixed" containerProps={{ className: 'overflow-visible' }}>
          <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
            {isCompact ? (
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead scope="col" className="w-9">
                  <Checkbox checked={selectAllState} onCheckedChange={handleSelectAll} aria-label="Select all" />
                </TableHead>
                <TableHead scope="col" className="h-auto w-[20%] py-1.5">
                  <div className="flex flex-col items-start gap-0.5">
                    <SortableHeader field="borrower_last_name">Borrower</SortableHeader>
                    <SortableHeader field="trans_details_loan_number">Loan #</SortableHeader>
                  </div>
                </TableHead>
                <TableHead scope="col" className="h-auto w-[16%] py-1.5">
                  <div className="flex flex-col items-start gap-0.5">
                    <SortableHeader field="milestone">Milestone</SortableHeader>
                    <span className={HEADER_LABEL_CLASS}>Loan Status</span>
                  </div>
                </TableHead>
                <TableHead scope="col" className="h-auto w-[13%] py-1.5">
                  <div className="flex flex-col items-start gap-0.5">
                    <SortableHeader field="trans_details_loan_amt">Amount</SortableHeader>
                    <SortableHeader field="trans_details_loan_purpose">Purpose</SortableHeader>
                  </div>
                </TableHead>
                <TableHead scope="col" className="h-auto py-1.5">
                  <SortableHeader field="property_address">Property</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="h-auto w-[13%] py-1.5">
                  <div className="flex flex-col items-start gap-0.5">
                    <SortableHeader field="loan_officer_last_name">Officer</SortableHeader>
                    <SortableHeader field="channel">Channel</SortableHeader>
                  </div>
                </TableHead>
                <TableHead scope="col" className="h-auto w-[13%] py-1.5">
                  <div className="flex flex-col items-start gap-0.5">
                    <SortableHeader field="rate_lock_status">Lock</SortableHeader>
                    <SortableHeader field="flag_days">Flag</SortableHeader>
                  </div>
                </TableHead>
                <TableHead scope="col" className="w-10 pl-0"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            ) : (
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead scope="col" className="w-10">
                  <Checkbox checked={selectAllState} onCheckedChange={handleSelectAll} aria-label="Select all" />
                </TableHead>
                <TableHead scope="col" className="w-[9.5%]">
                  <SortableHeader field="trans_details_loan_number">Loan #</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[10%]">
                  <SortableHeader field="borrower_last_name">Borrower</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[8.5%]">
                  <SortableHeader field="channel">Channel</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[9%]">
                  <SortableHeader field="milestone">Milestone</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[9%]">
                  {/* Not sortable: see SORT_FIELDS. */}
                  <span className={HEADER_LABEL_CLASS}>Loan Status</span>
                </TableHead>
                <TableHead scope="col" className="w-[7.5%]">
                  <SortableHeader field="trans_details_loan_amt">Amount</SortableHeader>
                </TableHead>
                <TableHead scope="col">
                  <SortableHeader field="property_address">Property</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[7%]">
                  <SortableHeader field="trans_details_loan_purpose">Purpose</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[8%]">
                  <SortableHeader field="loan_officer_last_name">Officer</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[8.5%]">
                  <SortableHeader field="rate_lock_status">Lock</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-[7.5%]">
                  <SortableHeader field="flag_days">Flag</SortableHeader>
                </TableHead>
                <TableHead scope="col" className="w-12"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            )}
          </TableHeader>
          {/* Rows dim during a refetch; the header stays usable. */}
          <TableBody className={cn(loadingClassName)}>{prospects.map(p => renderRow(p))}</TableBody>
        </Table>
        {loadMoreSentinel}
      </div>

      {footer}
    </div>
  );
}
